import { useCallback } from "react";
import { defaultRangeExtractor, type Range } from "@tanstack/react-virtual";

/**
 * The virtualizer's range plus the focused and dragged rows, so they stay
 * mounted while scrolled away. An index of -1 keeps nothing. The focused
 * index is read when the range is computed, so moving focus doesn't build a
 * new extractor: keyboard moves scroll, which recomputes the range.
 */
export const useKeptRowsRangeExtractor = ({
    getFocusedIndex,
    dragIndex,
}: {
    getFocusedIndex: () => number;
    dragIndex: number;
}) =>
    useCallback(
        (range: Range) => {
            const kept = [getFocusedIndex(), dragIndex].filter(
                (index) => index >= 0,
            );
            const indexes = defaultRangeExtractor(range);
            const indexSet = new Set(indexes);
            if (kept.every((index) => indexSet.has(index))) {
                return indexes;
            }
            return [...new Set([...indexes, ...kept])].sort((a, b) => a - b);
        },
        [getFocusedIndex, dragIndex],
    );
